import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { AppComponent } from './app.component';
import { Router, RouterModule, Routes } from '@angular/router';
import { PaymentMethodSelectionComponent } from './route/payment-method-selection/payment-method-selection.component';
import { NgxsModule } from '@ngxs/store';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { EmailAndInvoiceComponent } from './route/email-and-invoice/email-and-invoice.component';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoanEligibilityQuestionnaireComponent } from './route/loan-eligibility-questionnaire/loan-eligibility-questionnaire.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthComponent } from './route/auth/auth.component';
import { AuthState } from './ngxs/auth/auth.state';
import { PaymentRequestState } from './ngxs/payment-request/payment-request.state';
import { HeaderComponent } from './component/header/header.component';
import { FooterComponent } from './component/footer/footer.component';
import { PaymentRequestInfoComponent } from './component/payment-request-info/payment-request-info.component';
import { ExpandableListItemComponent } from './component/expandable-list-item/expandable-list-item.component';
import { FinsharkIframeComponent } from './component/finshark-iframe/finshark-iframe.component';
import { InstallmentsItemComponent } from './component/installments-item/installments-item.component';
import { SelectorComponent } from './component/selector/selector.component';
import { SpinnerComponent } from './component/spinner/spinner.component';
import { LeqSummaryNoComponent } from './component/leq-summary-no/leq-summary-no.component';
import { DARK_MODE_TOKEN } from 'web-component-library/projects/component-library/src/public-api';
import { DarkModeService } from '../app/service/theme-provider/darkmode.service';
import { AuthService } from './service/auth/auth.service';
import { CustomerJourneyService } from './service/customer-journey/customer-journey.service';
import { ErrorComponent } from './route/error/error.component';
import { CheckoutInterceptor } from './interceptors/checkout.interceptor';
import { SuccessComponent } from './route/success/success.component';
import { InstallmentsSelectionComponent } from './route/installments-selection/installments-selection.component';
import { InstallmentsState } from './ngxs/installments/installments.state';
import { TransferComponent } from './route/transfer/transfer.component';
import { ErrorState } from './ngxs/error/error.state';
import { InvoiceState } from './ngxs/invoice/invoice.state';
import { QueryParamsService } from './service/query-params/query-params.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CreditDecisionDialogComponent } from './component/dialog/credit-decision/credit-decision-dialog.component';
import { LoanEligibilityQuestionnaireSummaryComponent } from './route/loan-eligibility-questionnaire-summary/loan-eligibility-questionnaire-summary.component';
import { environment } from '../environments/environment';
import { CreditService } from './service/credit/credit.service';
import { CreditDecisionService } from './service/credit-decision/credit-decision.service';
import { SettleComponent } from './route/settle/settle.component';
import { AuthGuard } from './guard/auth.guard';
import { GuardRedirectService } from './service/redirect/guard-redirect.service';
import { PaymentRequestGuard } from './guard/payment-request.guard';
import { SuccessGuard } from './guard/success.guard';
import { CredentialsInterceptor } from './interceptors/credentials.interceptor';
import { HeaderService } from './service/header/header.service';
import { AppLoaderComponent } from './route/app-loader/app-loader.component';
import { PreventBackToSettlementOrAuthGuard } from './guard/prevent-back-to-settlement-or-auth-guard.service';
import { RoutingHistoryService } from './service/routing-history/routing-history.service';
import { SettlementService } from './service/settlement/settlement.service';
import {
  AtRoute,
  DeRoute,
  FiRoute,
  NoRoute,
  SeRoute,
  routeNames,
  routeParamNames,
} from '../assets/val/route-constants';
import { DatePickerComponent } from './component/date-picker/date-picker.component';
import { CalendarComponent } from './component/calendar/calendar.component';
import { PnrEntryComponent } from './route/pnr-entry/pnr-entry.component';
import { InvoiceConfirmComponent } from './route/invoice-confirm/invoice-confirm.component';
import { SwishComponent } from './route/swish/swish.component';
import { SwishService } from './service/swish/swish.service';
import { VippsComponent } from './route/NO/no-vipps/no-vipps.component';
import { VippsService } from './service/vipps/vipps.service';
import { ErrorDialogComponent as ErrorDialogComponent } from './component/dialog/error/error-dialog.component';
import { LoggingService } from './service/logging-service/logging.service';
import { ErrorResponseInterceptor } from './interceptors/error-response-interceptor';
import { NavigationCatcherComponent as NavigationErrorComponent } from './component/navigation-catcher/navigation-catcher.component';
import { InfoPrefillService } from '@app/service/info-prefill/info-prefill.service';
import { ComponentLibraryModule } from 'web-component-library/projects/component-library/src/public-api';
import { ValuePipe } from './shared/pipe/value.pipe';

import { StateService } from './service/state/state.service';
import { StateResolver } from './resolver/state.resolver';
import { IframeLandingStateResolver } from './resolver/iframe-landing-state.resolver';
import { FtsComponent } from './route/DE/de-fts/de-fts.component';
import { DePreFtsComponent } from './route/DE/de-pre-fts/de-pre-fts.component';

import localeSv from '@angular/common/locales/sv';
import localeDa from '@angular/common/locales/da';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeNb from '@angular/common/locales/nb';
import localeFi from '@angular/common/locales/fi';

import { InstantDebitService } from '@app/service/details/instant-debit.service';
import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { DeEmailAndPhoneRegistrationComponent } from './route/DE/de-email-and-phone-registration/de-email-and-phone-registration.component';
import { UserService } from './service/user-service/user.service';
import { DeUserRegistrationComponent } from './route/DE/de-user-registration/de-user-registration.component';
import { DePhoneVerificationComponent } from './route/DE/de-phone-verification/de-phone-verification.component';
import { PreFinAPIComponent } from './route/DE/de-pre-finapi/de-pre-finapi.component';
import { DeErrorComponent } from './route/DE/de-error/de-error.component';
import { DeSuccessComponent } from './route/DE/de-success/de-success.component';
import { DePartSuccessComponent } from './route/DE/de-part-success/de-part-success.component';
import { DeVerifyUserComponent } from './route/DE/de-verify-user/de-verify-user.component';
import { TrustlyDirectDebitComponent } from './route/trustly-direct-debit/trustly-direct-debit.component';
import { InstantDebitEmailComponent } from '@app/route/instant-debit-email/instant-debit-email.component';
import { DeEditPhoneComponent } from './route/DE/de-edit-phone/de-edit-phone.component';
import { DeRejectedDeComponent } from './route/DE/de-rejected/de-rejected.component';
import { DeEmailVerificationComponent } from './route/DE/de-email-verification/de-email-verification.component';
import { DePrecheckComponent } from './route/DE/de-precheck/de-precheck.component';
import { NoEmailAndInvoiceComponent } from './route/NO/no-email-and-invoice/no-email-and-invoice.component';
import { NoInvoiceConfirmComponent } from './route/NO/no-invoice-confirm/no-invoice-confirm.component';
import { NoSuccessComponent } from './route/NO/no-success/no-success.component';
import { NoInstallmentsSelectionComponent } from './route/NO/no-installments-selection/no-installments-selection.component';
import { DeInstallmentsSelectionComponent } from './route/DE/de-installments-selection/de-installments-selection.component';
import { NoErrorComponent } from './route/NO/no-error/no-error.component';
import { NoUserService } from './service/no-user-service/no-user.service';
import { PaymentMethodDetailService } from './service/payment-method-detail/payment-method-details.service';
import { DePrecheckSuccessComponent } from './route/DE/de-success/precheck/de-precheck-success.component';
import { DeCheckoutSuccessComponent } from './route/DE/de-success/checkout/de-checkout-success.component';
import { GeneralErrorComponent } from './component/general-error-de/general-error.component';
import { InputRefDirective } from './provider/trim-input-directive';
import { DeWrapperComponent } from './route/DE/de-wrapper-component/de-wrapper.component';
import { DeThinWrapperComponent } from './route/DE/de-thin-wrapper-component/de-thin-wrapper.component';
import { DeTokenizedOnboardingSuccessComponent } from './route/DE/de-success/tokenized-onboarding/de-tokenized-onboarding-success.component';
import { DeConfirmTokenizedPaymentComponent } from './route/DE/de-confirm-tokenized-payment/de-confirm-tokenized-payment.component';
import { DeFooterComponent } from './route/DE/de-footer/de-footer.component';
import { DeAccountRestorationComponent } from './route/DE/de-account-restoration/de-account-restoration.component';
import { UserFlowSelectionComponent } from './route/user-flow-selection/user-flow-selection.component';
import { RegisterStateResolver } from './resolver/register-state.resolver';
import { NoRejectedComponent } from './route/NO/no-rejected/no-rejected.component';
import { NoAuthComponent } from './route/NO/no-auth/no-auth.component';
import { DeRegisterIbanComponent } from './route/DE/de-register-iban/de-register-iban.component';
import { NoSalaryCheckComponent } from './route/NO/no-salary-check/no-salary-check.component';
import { DeCollectiveInvoiceSuccessComponent } from './route/DE/de-success/collective-invoice/de-collective-invoice-success.component';
import { DeConfirmCollectiveInvoice } from './route/DE/de-confirm-collective-invoice/de-confirm-collective-invoice.component';
import { FinAPIComponent } from './route/DE/de-finapi/de-finapi.component';
import { FinAPIUpdateComponent } from './route/DE/de-finapi-update/de-finapi-update.component';
import { FinAPIPaymentComponent } from './route/DE/de-finapi-payment/de-finapi-payment.component';
import { DeSelectIbanComponent } from './route/DE/de-select-iban/de-select-iban.component';
import { RedirectService } from '@app/service/redirect-service/redirect.service';
import { SePrecheckComponent } from './route/SE/se-precheck/se-precheck.component';
import { AuthenticateComponent } from './route/SE/authenticate/authenticate.component';
import { BankMandateComponent } from './route/SE/bank-mandate/bank-mandate.component';
import { SeEmailAndPhoneRegistrationComponent } from './route/SE/se-email-and-phone-registration/se-email-and-phone-registration.component';
import { SeConfirmTokenizedPaymentComponent } from './route/SE/se-confirm-tokenized-payment/se-confirm-tokenized-payment.component';
import { SeSuccessPageComponent } from './route/SE/se-success-page/se-success-page.component';
import { SeTransitionPollComponent } from './route/SE/se-transition-poll/se-transition-poll.component';
import { VendorFlowComponent } from './route/SE/vendor-flow/vendor-flow.component';
import { SeConfirmTokenizedOnboardingComponent } from './route/SE/confirm_tokenized_onboarding_invoice/confirm_tokenized_onboarding_invoice.component';
import { TermsLinkService } from './service/terms-link/terms-link.service';
import { SeTermsComponent } from './route/se-terms-component/se-terms.component';
import { FiAuthenticateComponent } from './route/FI/fi-authenticate/fi-authenticate.component';
import { FiEmailAndPhoneRegistrationComponent } from './route/FI/fi-email-and-phone-registration/fi-email-and-phone-registration.component';
import { FiInvoiceConfirmComponent } from './route/FI/fi-invoice-confirm/fi-invoice-confirm.component';
import { FiSuccessComponent } from './route/FI/fi-success/fi-success.component';
import { FiInstallmentsSelectionComponent } from './route/FI/fi-installments-selection/fi-installments-selection.component';
import { FiErrorComponent } from './route/FI/fi-error/fi-error.component';
import { FiRejectedComponent } from './route/FI/fi-rejected/fi-rejected.component';
import { PiaEmailAndPhoneRegistrationComponent } from './route/MISC/pia-email-and-phone/pia-email-and-phone-registration.component';
import { PiaPhoneVerificationComponent } from './route/MISC/pia-confirm-phone/pia-phone-verification.component';
import { EmailVerificationComponent } from './route/MISC/pia-confirm-email/pia-email-verification.component';
import { PIAService } from './service/pay-in-advance/pay-in-advance.service';
import { PiaSuccessComponent } from './route/MISC/pia-success/pia-success.component';
import { PiaPaymentMethodSelectionComponent } from './route/MISC/pia-payment-method-selection/pia-payment-method-selection.component';
import { FinsharkComponent } from './route/finshark/finshark.component';
import { IframeService } from './service/iframe/iframe.service';
import { LoaderMessagingService } from './service/loader-messaging/loader-messaging.service';
import { FiLoanEligibilityQuestionnaireBaseComponent } from './route/FI/fi-loan-eligibility-questionnaire-base/fi-loan-eligibility-questionnaire-base.component';
import { FiLoanEligibilityQuestionnaireExtendedComponent } from './route/FI/fi-loan-eligibility-questionnaire-extended/fi-loan-eligibility-questionnaire-extended.component';
import { FiLeqReviewComponent } from './route/FI/fi-loan-eligibility-questionnaire-review/fi-loan-eligibility-questionnaire-review.component';
import { FiAuthenticateScriveComponent } from './route/FI/fi-authenticate-scrive/fi-authenticate-scrive.component';
import { IframeLandingComponent } from './route/iframe-landing/iframe-landing.component';
import { LoanCalculatorComponent } from './route/loan-calculator/loan-calculator.component';
import { LoanCalculatorService } from './service/loan-calculator/loan-calculator.service';
import { KustomLandingComponent } from './route/kustom/kustom-landing/kustom-landing.component';
import { KustomService } from './service/kustom/kustom.service';
import { NoLoanEligibilityQuestionnaireComponent } from './route/NO/no-loan-eligibility-questionnaire/no-loan-eligibility-questionnaire.component';
import { NoLeqReviewComponent } from './route/NO/no-loan-eligibility-questionnaire-review/no-loan-eligibility-questionnaire-review.component';
import { StateSyncService } from './service/state-sync/state-sync.service';
import { IbanFormatPipe } from '@app/shared/pipe/iban.format.pipe';
import { IbanFormatterDirective } from '@app/shared/directive/iban.formatter.directive';
import { KreditzComponent } from './route/SE/kreditz/kreditz.component';
import { KreditzIframeComponent } from './component/kreditz-iframe/kreditz-iframe.component';
import { SeAisAccountComponent } from './route/SE/in-house/se-ais-account/se-ais-account.component';
import { SeConfirmInHouseTokenizedOnboardingComponent } from './route/SE/in-house/se-confirm-in-house-onboarding/se-confirm-in-house-onboarding.component';
import { SeAisCallbackComponent } from './route/SE/in-house/se-ais-callback/se-ais-callback.component';
import { SeConnectBankStart } from './route/SE/se-connect-to-bank-start/se-connect-to-bank-start.component';
import { SeInstantDebitInHouseComponent } from './route/SE/in-house/se-instant-debit-in-house/se-instant-debit-in-house.component';

registerLocaleData(localeSv);
registerLocaleData(localeDa);
registerLocaleData(localeEn);
registerLocaleData(localeDe);
registerLocaleData(localeNb);
registerLocaleData(localeFi);

const appRoutes: Routes = [
  {
    path: routeNames.EMAIL_AND_INVOICE,
    component: EmailAndInvoiceComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.FINSHARK,
    component: FinsharkComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.TRUSTLY_DIRECT_DEBIT,
    component: TrustlyDirectDebitComponent,
    canActivate: [PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.TRUSTLY_DIRECT_DEBIT_EMAIL,
    component: InstantDebitEmailComponent,
    canActivate: [PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.INVOICE_CONFIRM,
    component: InvoiceConfirmComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.LEQ_SUMMARY,
    component: LoanEligibilityQuestionnaireSummaryComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
  },
  {
    path: routeNames.LEQ,
    component: LoanEligibilityQuestionnaireComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
  },
  {
    path: routeNames.SELECT_INSTALLMENT_PLAN,
    component: InstallmentsSelectionComponent,
    canActivate: [AuthGuard, PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.PERSONAL_NUMBER_ENTRY,
    component: PnrEntryComponent,
    canActivate: [PaymentRequestGuard],
  },
  {
    path: routeNames.BANK_TRANSFER_FRAME,
    component: TransferComponent,
    canActivate: [PaymentRequestGuard],
    canDeactivate: [PreventBackToSettlementOrAuthGuard],
  },
  {
    path: routeNames.SETTLE,
    component: SettleComponent,
    canActivate: [PaymentRequestGuard],
  },
  {
    path: routeNames.SUCCESS,
    component: SuccessComponent,
    canActivate: [SuccessGuard],
    pathMatch: 'full',
  },
  {
    path: routeNames.SWISH,
    component: SwishComponent,
    canActivate: [PaymentRequestGuard],
    pathMatch: 'full',
  },
  {
    path: routeNames.HOME,
    component: PaymentMethodSelectionComponent,
    canActivate: [PaymentRequestGuard],
    pathMatch: 'full',
    data: {
      requireAuth: false,
      requirePaymentMethodSelected: false,
    },
  },
  {
    path: routeNames.AUTH,
    component: AuthComponent,
    canActivate: [PaymentRequestGuard],
  },
  {
    path: 'app-loader',
    component: AppLoaderComponent,
    pathMatch: 'full',
  },
  {
    path: routeNames.ERROR,
    component: ErrorComponent,
  },
  {
    path: AtRoute.HOME,
    resolve: {
      state: RegisterStateResolver,
    },
    component: UserFlowSelectionComponent,
  },
  {
    path: AtRoute.EMAIL_AND_PHONE_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: DeEmailAndPhoneRegistrationComponent,
  },
  {
    path: AtRoute.PHONE_VERIFICATION,
    resolve: {
      state: StateResolver,
    },
    component: DePhoneVerificationComponent,
  },
  {
    path: AtRoute.USER_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: DeUserRegistrationComponent,
  },
  {
    path: AtRoute.FINAPI,
    resolve: {
      state: StateResolver,
    },
    component: FinAPIComponent,
  },
  {
    path: AtRoute.PRE_FINAPI,
    resolve: {
      state: StateResolver,
    },
    component: PreFinAPIComponent,
  },
  {
    path: AtRoute.FINAPI_UPDATE,
    resolve: {
      state: StateResolver,
    },
    component: FinAPIUpdateComponent,
  },
  {
    path: AtRoute.FINAPI_PAYMENT,
    resolve: {
      state: StateResolver,
    },
    data: {
      hideCancelButton: true,
    },
    component: FinAPIPaymentComponent,
  },
  {
    path: AtRoute.FTS,
    resolve: {
      state: StateResolver,
    },
    component: FtsComponent,
  },
  {
    path: AtRoute.PRE_FTS,
    resolve: {
      state: StateResolver,
    },
    component: DePreFtsComponent,
  },
  {
    path: AtRoute.SELECT_IBAN,
    resolve: {
      state: StateResolver,
    },
    component: DeSelectIbanComponent,
  },
  {
    path: AtRoute.PART_SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: DePartSuccessComponent,
  },
  {
    path: AtRoute.VERIFY_USER,
    resolve: {
      state: StateResolver,
    },
    component: DeVerifyUserComponent,
  },
  {
    path: AtRoute.SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: DeSuccessComponent,
  },
  {
    path: AtRoute.RESTORE_ACCOUNT,
    resolve: {
      state: StateResolver,
    },
    component: DeAccountRestorationComponent,
  },
  {
    path: AtRoute.EDIT_PHONE,
    resolve: {
      state: StateResolver,
    },
    component: DeEditPhoneComponent,
  },
  {
    path: AtRoute.ERROR,
    resolve: {
      state: StateResolver,
    },
    component: DeErrorComponent,
  },
  {
    path: AtRoute.REJECTED,
    resolve: {
      state: StateResolver,
    },
    component: DeRejectedDeComponent,
  },
  {
    path: AtRoute.VERIFY_EMAIL,
    resolve: {
      state: StateResolver,
    },
    component: DeEmailVerificationComponent,
  },
  {
    path: AtRoute.PRECHECK,
    resolve: {
      state: RegisterStateResolver,
    },
    component: DePrecheckComponent,
  },
  {
    path: AtRoute.INSTALLMENTS_SELECTION,
    resolve: {
      state: StateResolver,
    },
    component: DeInstallmentsSelectionComponent,
  },
  {
    path: AtRoute.CONFIRM_COLLECTIVE_INVOICE,
    resolve: {
      state: StateResolver,
    },
    component: DeConfirmCollectiveInvoice,
  },
  {
    path: AtRoute.REGISTER_IBAN,
    resolve: {
      state: StateResolver,
    },
    component: DeRegisterIbanComponent,
  },
  {
    path: AtRoute.CONFIRM_TOKENIZED_PAYMENT,
    resolve: {
      state: StateResolver,
    },
    component: DeConfirmTokenizedPaymentComponent,
  },
  {
    path: DeRoute.HOME,
    resolve: {
      state: RegisterStateResolver,
    },
    component: UserFlowSelectionComponent,
  },
  {
    path: DeRoute.EMAIL_AND_PHONE_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: DeEmailAndPhoneRegistrationComponent,
  },
  {
    path: DeRoute.PHONE_VERIFICATION,
    resolve: {
      state: StateResolver,
    },
    component: DePhoneVerificationComponent,
  },
  {
    path: DeRoute.USER_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: DeUserRegistrationComponent,
  },
  {
    path: DeRoute.FTS,
    resolve: {
      state: StateResolver,
    },
    component: FtsComponent,
  },
  {
    path: DeRoute.PRE_FTS,
    resolve: {
      state: StateResolver,
    },
    component: DePreFtsComponent,
  },
  {
    path: DeRoute.FINAPI,
    resolve: {
      state: StateResolver,
    },
    component: FinAPIComponent,
  },
  {
    path: DeRoute.PRE_FINAPI,
    resolve: {
      state: StateResolver,
    },
    component: PreFinAPIComponent,
  },
  {
    path: DeRoute.FINAPI_UPDATE,
    resolve: {
      state: StateResolver,
    },
    component: FinAPIUpdateComponent,
  },
  {
    path: DeRoute.FINAPI_PAYMENT,
    resolve: {
      state: StateResolver,
    },
    data: {
      hideCancelButton: true,
    },
    component: FinAPIPaymentComponent,
  },
  {
    path: DeRoute.SELECT_IBAN,
    resolve: {
      state: StateResolver,
    },
    component: DeSelectIbanComponent,
  },
  {
    path: DeRoute.PART_SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: DePartSuccessComponent,
  },
  {
    path: DeRoute.VERIFY_USER,
    resolve: {
      state: StateResolver,
    },
    component: DeVerifyUserComponent,
  },
  {
    path: DeRoute.CONFIRM_COLLECTIVE_INVOICE,
    resolve: {
      state: StateResolver,
    },
    component: DeConfirmCollectiveInvoice,
  },
  {
    path: DeRoute.SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: DeSuccessComponent,
  },
  {
    path: DeRoute.RESTORE_ACCOUNT,
    resolve: {
      state: StateResolver,
    },
    component: DeAccountRestorationComponent,
  },
  {
    path: DeRoute.EDIT_PHONE,
    resolve: {
      state: StateResolver,
    },
    component: DeEditPhoneComponent,
  },
  {
    path: DeRoute.ERROR,
    resolve: {
      state: StateResolver,
    },
    component: DeErrorComponent,
  },
  {
    path: DeRoute.REJECTED,
    resolve: {
      state: StateResolver,
    },
    component: DeRejectedDeComponent,
  },
  {
    path: DeRoute.VERIFY_EMAIL,
    resolve: {
      state: StateResolver,
    },
    component: DeEmailVerificationComponent,
  },
  {
    path: DeRoute.PRECHECK,
    resolve: {
      state: RegisterStateResolver,
    },
    component: DePrecheckComponent,
  },
  {
    path: DeRoute.INSTALLMENTS_SELECTION,
    resolve: {
      state: StateResolver,
    },
    component: DeInstallmentsSelectionComponent,
  },
  {
    path: DeRoute.CONFIRM_TOKENIZED_PAYMENT,
    resolve: {
      state: StateResolver,
    },
    component: DeConfirmTokenizedPaymentComponent,
  },
  {
    path: DeRoute.REGISTER_IBAN,
    resolve: {
      state: StateResolver,
    },
    component: DeRegisterIbanComponent,
  },
  {
    path: FiRoute.HOME,
    resolve: {
      state: RegisterStateResolver,
    },
    component: UserFlowSelectionComponent,
  },
  {
    path: FiRoute.AUTHENTICATE,
    resolve: {
      state: StateResolver,
    },
    component: FiAuthenticateComponent,
  },
  {
    path: FiRoute.AUTHENTICATE_SCRIVE,
    resolve: {
      state: StateResolver,
    },
    component: FiAuthenticateScriveComponent,
  },
  {
    path: FiRoute.BASE_KALP_FI,
    resolve: {
      state: StateResolver,
    },
    component: FiLoanEligibilityQuestionnaireBaseComponent,
  },
  {
    path: FiRoute.EXTENDED_KALP_FI,
    resolve: {
      state: StateResolver,
    },
    component: FiLoanEligibilityQuestionnaireExtendedComponent,
  },
  {
    path: FiRoute.KALP_REVIEW_ANSWERS,
    resolve: {
      state: StateResolver,
    },
    component: FiLeqReviewComponent,
  },
  {
    path: FiRoute.EMAIL_AND_PHONE_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: FiEmailAndPhoneRegistrationComponent,
  },
  {
    path: FiRoute.INSTALLMENTS_SELECTION,
    resolve: {
      state: StateResolver,
    },
    component: FiInstallmentsSelectionComponent,
  },
  {
    path: FiRoute.CONFIRM_INVOICE,
    resolve: {
      state: StateResolver,
    },
    component: FiInvoiceConfirmComponent,
  },
  {
    path: FiRoute.SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: FiSuccessComponent,
  },
  {
    path: FiRoute.ERROR,
    resolve: {
      state: StateResolver,
    },
    component: FiErrorComponent,
  },
  {
    path: FiRoute.REJECTED,
    resolve: {
      state: StateResolver,
    },
    component: FiRejectedComponent,
  },
  {
    path: NoRoute.HOME,
    resolve: {
      state: RegisterStateResolver,
    },
    component: UserFlowSelectionComponent,
  },
  {
    path: NoRoute.EMAIL_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: NoEmailAndInvoiceComponent,
  },
  {
    path: NoRoute.KALP,
    resolve: {
      state: StateResolver,
    },
    component: NoLoanEligibilityQuestionnaireComponent,
  },
  {
    path: NoRoute.KALP_REVIEW_ANSWERS,
    resolve: {
      state: StateResolver,
    },
    component: NoLeqReviewComponent,
  },
  {
    path: NoRoute.SALARY_CHECK,
    resolve: {
      state: StateResolver,
    },
    component: NoSalaryCheckComponent,
  },
  {
    path: NoRoute.CONFIRM_INVOICE,
    resolve: {
      state: StateResolver,
    },
    component: NoInvoiceConfirmComponent,
  },
  {
    path: NoRoute.INSTALLMENTS_SELECTION,
    resolve: {
      state: StateResolver,
    },
    component: NoInstallmentsSelectionComponent,
  },
  {
    path: NoRoute.SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: NoSuccessComponent,
  },
  {
    path: NoRoute.NO_BANKID,
    resolve: {
      state: StateResolver,
    },
    component: NoAuthComponent,
  },
  {
    path: NoRoute.ERROR,
    resolve: {
      state: StateResolver,
    },
    component: NoErrorComponent,
  },
  {
    path: NoRoute.REJECTED,
    resolve: {
      state: StateResolver,
    },
    component: NoRejectedComponent,
  },
  {
    path: DeRoute.INSTALLMENTS_SELECTION,
    resolve: {
      state: StateResolver,
    },
    component: DeInstallmentsSelectionComponent,
  },
  {
    path: NoRoute.NO_VIPPS,
    resolve: {
      state: StateResolver,
    },
    component: VippsComponent,
  },

  {
    path: SeRoute.HOME,
    resolve: {
      state: RegisterStateResolver,
    },
    // Note that this component should be the "UserFlowSelectionComponent" like the other markets.
    // However until backend driven flows are fully implemented in Sweden this one acts as a fallback
    // only fully backend driven flows should go in there, otherwise it redirects to the frontend-driven selector.
    component: PiaPaymentMethodSelectionComponent,
  },
  {
    path: SeRoute.PRECHECK,
    resolve: {
      state: RegisterStateResolver,
    },
    component: SePrecheckComponent,
  },
  {
    path: SeRoute.AUTHENTICATE,
    resolve: {
      state: StateResolver,
    },
    component: AuthenticateComponent,
  },
  {
    path: SeRoute.BANK_ID_SIGN,
    resolve: {
      state: StateResolver,
    },
    component: AuthenticateComponent,
  },
  {
    path: SeRoute.BANK_MANDATE,
    resolve: {
      state: StateResolver,
    },
    component: BankMandateComponent,
  },
  {
    path: SeRoute.SE_BANK_START,
    resolve: {
      state: StateResolver,
    },
    component: SeConnectBankStart,
  },
  {
    path: SeRoute.SELECT_AIS_ACCOUNT,
    resolve: {
      state: StateResolver,
    },
    component: SeAisAccountComponent,
  },
  {
    path: SeRoute.SELECT_AIS_CALLBACK,
    component: SeAisCallbackComponent,
  },
  {
    path: SeRoute.VENDOR_FLOW,
    resolve: {
      state: StateResolver,
    },
    component: VendorFlowComponent,
  },
  {
    path: SeRoute.EMAIL_AND_PHONE_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: SeEmailAndPhoneRegistrationComponent,
  },
  {
    path: SeRoute.PIA_EMAIL_AND_PHONE_REGISTRATION,
    resolve: {
      state: StateResolver,
    },
    component: PiaEmailAndPhoneRegistrationComponent,
  },
  {
    path: SeRoute.PIA_PHONE_VERIFICATION,
    resolve: {
      state: StateResolver,
    },
    component: PiaPhoneVerificationComponent,
  },
  {
    path: SeRoute.PIA_VERIFY_EMAIL,
    resolve: {
      state: StateResolver,
    },
    component: EmailVerificationComponent,
  },
  {
    path: SeRoute.CONFIRM_IN_HOUSE_ONBOARDING,
    resolve: {
      state: StateResolver,
    },
    component: SeConfirmInHouseTokenizedOnboardingComponent,
  },
  {
    path: SeRoute.CONFIRM_IN_HOUSE_INSTANT_DEBIT,
    resolve: {
      state: StateResolver,
    },
    component: SeInstantDebitInHouseComponent,
  },
  {
    path: SeRoute.CONFIRM_TOKENIZED_PAYMENT,
    resolve: {
      state: StateResolver,
    },
    component: SeConfirmTokenizedPaymentComponent,
  },
  {
    path: SeRoute.CONFIRM_TOKENIZED_ONBOARDING_INVOICE,
    resolve: {
      state: StateResolver,
    },
    component: SeConfirmTokenizedOnboardingComponent,
  },
  {
    path: SeRoute.SUCCESS,
    resolve: {
      state: StateResolver,
    },
    component: SeSuccessPageComponent,
  },
  {
    path: SeRoute.ERROR,
    resolve: {
      state: StateResolver,
    },
    component: DeErrorComponent,
  },
  {
    path: SeRoute.REJECTED,
    resolve: {
      state: StateResolver,
    },
    component: DeRejectedDeComponent,
  },
  {
    path: SeRoute.TRANSITION,
    component: SeTransitionPollComponent,
  },
  {
    path: SeRoute.KREDITZ,
    resolve: {
      state: StateResolver,
    },
    component: KreditzComponent,
  },

  // KUSTOM INTEGRATION
  {
    path: 'eoi',
    component: KustomLandingComponent,
  },

  // IFRAME/LOADER LANDING
  {
    path: 'iframe-landing',
    component: IframeLandingComponent,
    resolve: {
      state: IframeLandingStateResolver,
    },
  },
  {
    path: 'landing', // TEMPORARY for users still running the old loader
    component: IframeLandingComponent,
    resolve: {
      state: IframeLandingStateResolver,
    },
  },

  // Loan Calculator Routes
  {
    path: `${routeNames.LOAN_CALCULATOR}/:${routeParamNames.QR_CODE}`,
    component: LoanCalculatorComponent,
  },
  {
    path: `${routeNames.LOAN_CALCULATOR}/:${routeParamNames.QR_CODE}/:${routeParamNames.LOCALE}`,
    component: LoanCalculatorComponent,
  },

  // Navigation error catcher
  {
    path: '**',
    component: NavigationErrorComponent,
  },
];

const devModules = environment.production
  ? []
  : [
      /**
       * Log ngxs mutations
       */
      NgxsLoggerPluginModule.forRoot(),
    ];

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    PaymentMethodSelectionComponent,
    ExpandableListItemComponent,
    FinsharkIframeComponent,
    KreditzIframeComponent,
    LeqSummaryNoComponent,
    InstallmentsItemComponent,
    SelectorComponent,
    PaymentRequestInfoComponent,
    EmailAndInvoiceComponent,
    FinsharkComponent,
    InvoiceConfirmComponent,
    LoanEligibilityQuestionnaireComponent,
    AuthComponent,
    SpinnerComponent,
    ErrorComponent,
    SuccessComponent,
    InstallmentsSelectionComponent,
    TransferComponent,
    CreditDecisionDialogComponent,
    ErrorDialogComponent,
    LoanEligibilityQuestionnaireSummaryComponent,
    SettleComponent,
    AppLoaderComponent,
    PnrEntryComponent,
    SwishComponent,
    VippsComponent,
    IframeLandingComponent,
    KustomLandingComponent,
    NavigationErrorComponent,
    ValuePipe,
    DeUserRegistrationComponent,
    FtsComponent,
    DeEmailAndPhoneRegistrationComponent,
    DePhoneVerificationComponent,
    PreFinAPIComponent,
    DeErrorComponent,
    DeSuccessComponent,
    DePartSuccessComponent,
    DeVerifyUserComponent,
    DeEditPhoneComponent,
    TrustlyDirectDebitComponent,
    InstantDebitEmailComponent,
    DeRejectedDeComponent,
    DePrecheckComponent,
    DeEmailVerificationComponent,
    DePrecheckSuccessComponent,
    DeCheckoutSuccessComponent,
    GeneralErrorComponent,
    InputRefDirective,
    NoEmailAndInvoiceComponent,
    NoAuthComponent,
    NoInvoiceConfirmComponent,
    NoErrorComponent,
    NoSuccessComponent,
    DeWrapperComponent,
    DeThinWrapperComponent,
    DeTokenizedOnboardingSuccessComponent,
    DeConfirmTokenizedPaymentComponent,
    DeFooterComponent,
    DeAccountRestorationComponent,
    DeInstallmentsSelectionComponent,
    NoInstallmentsSelectionComponent,
    UserFlowSelectionComponent,
    NoRejectedComponent,
    DeRegisterIbanComponent,
    NoSalaryCheckComponent,
    DeCollectiveInvoiceSuccessComponent,
    DeConfirmCollectiveInvoice,
    FinAPIComponent,
    DePreFtsComponent,
    FinAPIUpdateComponent,
    FinAPIPaymentComponent,
    DeSelectIbanComponent,
    SePrecheckComponent,
    AuthenticateComponent,
    BankMandateComponent,
    SeEmailAndPhoneRegistrationComponent,
    SeConfirmTokenizedPaymentComponent,
    SeSuccessPageComponent,
    SeTransitionPollComponent,
    VendorFlowComponent,
    SeConfirmTokenizedOnboardingComponent,
    SeTermsComponent,
    SeConnectBankStart,
    SeAisAccountComponent,
    SeAisCallbackComponent,
    SeConfirmInHouseTokenizedOnboardingComponent,
    SeInstantDebitInHouseComponent,
    FiAuthenticateComponent,
    FiAuthenticateScriveComponent,
    FiEmailAndPhoneRegistrationComponent,
    FiInvoiceConfirmComponent,
    FiSuccessComponent,
    FiInstallmentsSelectionComponent,
    FiErrorComponent,
    FiRejectedComponent,
    PiaEmailAndPhoneRegistrationComponent,
    PiaPhoneVerificationComponent,
    EmailVerificationComponent,
    PiaSuccessComponent,
    PiaPaymentMethodSelectionComponent,
    FiLoanEligibilityQuestionnaireBaseComponent,
    FiLoanEligibilityQuestionnaireExtendedComponent,
    FiLeqReviewComponent,
    DatePickerComponent,
    CalendarComponent,
    LoanCalculatorComponent,
    NoLoanEligibilityQuestionnaireComponent,
    NoLeqReviewComponent,
    IbanFormatPipe,
    IbanFormatterDirective,
    KreditzComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'checkout' }),
    BrowserAnimationsModule,
    MatInputModule,
    MatIconModule,
    MatFormFieldModule,
    MatDialogModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    ComponentLibraryModule,
    /**
     * Configure app ngxs management with NgXS.
     */
    NgxsModule.forRoot([
      AuthState,
      PaymentRequestState,
      InstallmentsState,
      ErrorState,
      InvoiceState,
    ]),
    /**
     * Utilize ngxs binding for forms
     */
    NgxsFormPluginModule.forRoot(),
    RouterModule.forRoot(appRoutes, {
      enableTracing: environment.logSettings.routerLog,
    }),
    ...devModules,
  ],
  providers: [
    CustomerJourneyService,
    AuthService,
    SettlementService,
    QueryParamsService,
    GuardRedirectService,
    CreditService,
    CreditDecisionService,
    HeaderService,
    AuthGuard,
    PaymentRequestGuard,
    SuccessGuard,
    RoutingHistoryService,
    SwishService,
    VippsService,
    LoggingService,
    InfoPrefillService,
    StateService,
    NoUserService,
    PaymentMethodDetailService,
    StateResolver,
    RegisterStateResolver,
    IframeLandingStateResolver,
    InstantDebitService,
    UserService,
    CurrencyPipe,
    ValuePipe,
    RedirectService,
    TermsLinkService,
    PIAService,
    IframeService,
    LoaderMessagingService,
    LoanCalculatorService,
    KustomService,
    StateSyncService,
    DarkModeService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CheckoutInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorResponseInterceptor,
      multi: true,
    },
    {
      provide: DARK_MODE_TOKEN,
      useExisting: DarkModeService,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        disableClose: true,
        closeOnNavigation: true,
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
  ],
  bootstrap: [AppComponent],
  entryComponents: [CreditDecisionDialogComponent, ErrorDialogComponent],
})
export class AppModule {}
